import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Facebook, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Plug, Download, BarChart3, DollarSign, Trophy, Send, Sparkles, Clock, Pause, Users, Target, Zap, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sr } from "../../lib/sr";
import { auth } from "../../lib/api";
import { useCurrency } from "../../lib/useCurrency";

export default function MetaAdsWorkspace() {
  const nav = useNavigate();
  const isAdmin = ["super_admin", "admin"].includes((auth.getUser() || {}).role);
  const cur = useCurrency();
  const [status, setStatus] = useState(null);
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [form, setForm] = useState({ ad_account_id: "", page_id: "", instagram_actor_id: "", dataset_id: "" });
  const [igs, setIgs] = useState([]);
  const [pixels, setPixels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [campaigns, setCampaigns] = useState(null);
  const [insights, setInsights] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [leads, setLeads] = useState(null);
  const [builder, setBuilder] = useState(null); // connection id when open
  const [billing, setBilling] = useState(null);
  const [adminCfg, setAdminCfg] = useState(null); // null=not staff/unknown; object=staff config
  const [cfgForm, setCfgForm] = useState({ app_id: "", business_id: "", app_secret: "", system_token: "" });
  const [cfgBusy, setCfgBusy] = useState(false);
  const [cfgTest, setCfgTest] = useState(null); // {ok, message/error}
  const [oauthSel, setOauthSel] = useState(null); // {cid, ad_accounts, pages, ad_account_id, page_id}
  const [published, setPublished] = useState([]);
  const [report, setReport] = useState(null); // {linkId, ...report}
  const [posts, setPosts] = useState([]);
  const [growth, setGrowth] = useState({ audiences: [], saved: [], recs: null, capi: null, pixelInput: "", audName: "", busy: false, loaded: false });
  const [invoices, setInvoices] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [postForm, setPostForm] = useState({ topic: "", message: "", image_url: "", link: "", connectionId: "", scheduleAt: "", generating: false, publishing: false });
  const [budgetInputs, setBudgetInputs] = useState({});
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const emptyDraft = { name: "", objective: "OUTCOME_TRAFFIC", country: "US", age_min: 25, age_max: 55, budget_type: "daily", budget_amount: 50, primary_text: "", headline: "", link_url: "" };
  const [draft, setDraft] = useState(emptyDraft);

  const loadDrafts = async () => { try { const r = await sr.metaDrafts(); setDrafts(r.drafts || []); } catch { /* noop */ } };
  const loadLeads = async () => { try { const r = await sr.metaLeads(); setLeads(r.leads || []); } catch { setLeads([]); } };
  const saveDraft = async (cid) => {
    setBusy(true);
    try {
      await sr.metaCreateDraft({
        connection_id: cid, name: draft.name, objective: draft.objective,
        audience: { countries: [draft.country], age_min: Number(draft.age_min), age_max: Number(draft.age_max) },
        budget: { type: draft.budget_type, amount: Number(draft.budget_amount) },
        creative: { primary_text: draft.primary_text, headline: draft.headline, link_url: draft.link_url, cta: "LEARN_MORE" },
      });
      toast.success("Draft saved"); setBuilder(null); setDraft(emptyDraft); loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); } finally { setBusy(false); }
  };
  const publishDraft = async (d) => {
    try {
      const chk = await sr.metaChecklist(d.id);
      if (!chk.all_pass) { toast.error("Go-live checklist has unresolved items"); return; }
      if (!window.confirm(`Publish "${d.name}" to Meta as PAUSED? It will not spend until you activate it.`)) return;
      await sr.metaPublish(d.id); toast.success("Published to Meta (PAUSED)"); loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([sr.metaStatus(), sr.metaConnections()]);
      setStatus(s); setConns(c.connections || []);
      if ((c.connections || []).length) { loadDrafts(); loadLeads(); loadPublished(); loadPosts(); loadGrowth(c.connections[0].id); }
    } catch (e) { /* not configured / no access */ }
    try { setBilling(await sr.metaBillingCurrent()); } catch { /* noop */ }
    try { const cfg = await sr.metaAdminConfigGet(); setAdminCfg(cfg); setCfgForm({ app_id: cfg.app_id || "", business_id: cfg.business_id || "", app_secret: "", system_token: "" }); } catch { setAdminCfg(null); }
    try { const r = await sr.metaEmailHistory(); setEmailHistory(r.history || []); } catch { /* noop */ }
    try { const r = await sr.metaBillingInvoices(); setInvoices(r.invoices || []); } catch { /* noop */ }
    setLoading(false);
  };
  const emailInvoice = async (id) => { try { const r = await sr.metaEmailInvoice(id); r.ok ? toast.success("Statement emailed") : toast.info(r.delivery?.error || "Not sent"); const h = await sr.metaEmailHistory(); setEmailHistory(h.history || []); } catch (e) { toast.error(e?.response?.data?.detail || "Could not email"); } };
  const generateInvoice = async (month) => { try { await sr.metaBillingGenerate(month); toast.success(`Invoice finalized for ${month}`); const r = await sr.metaBillingInvoices(); setInvoices(r.invoices || []); } catch (e) { toast.error(e?.response?.data?.detail || "Could not finalize"); } };
  const downloadInvoicePdf = async (inv) => { try { const blob = await sr.metaInvoicePdf(inv.id); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `meta-invoice-${inv.month}.pdf`; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Could not download PDF"); } };
  const loadPublished = async () => { try { const r = await sr.metaPublished(); setPublished(r.campaigns || []); } catch { /* noop */ } };
  const loadPosts = async () => { try { const r = await sr.metaListPosts(); setPosts(r.posts || []); } catch { /* noop */ } };
  const openReport = async (linkId) => {
    if (report?.linkId === linkId) { setReport(null); return; }
    setReport({ linkId, loading: true });
    try { const r = await sr.metaAbReport(linkId); setReport({ linkId, ...r }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load report"); setReport(null); }
  };
  const autoPause = async (linkId) => {
    try { const r = await sr.metaAutoPause(linkId); r.acted ? toast.success(`Paused ${r.paused_ads.length} losing ad(s)`) : toast.info(r.reason || "No action taken"); loadPublished(); if (report?.linkId === linkId) openReport(linkId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not auto-pause"); }
  };
  const activateLink = async (linkId) => { try { await sr.metaActivate(linkId); toast.success("Campaign activated"); loadPublished(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not activate"); } };
  const pauseLink = async (linkId) => { try { await sr.metaPause(linkId); toast.success("Campaign paused"); loadPublished(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not pause"); } };
  const genPost = async () => {
    setPostForm((f) => ({ ...f, generating: true }));
    try { const r = await sr.metaGenPost({ topic: postForm.topic }); setPostForm((f) => ({ ...f, message: r.post.message, generating: false })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); setPostForm((f) => ({ ...f, generating: false })); }
  };
  const publishPost = async () => {
    const cid = postForm.connectionId || (conns[0]?.id);
    if (!cid) { toast.error("Connect a Facebook Page first"); return; }
    setPostForm((f) => ({ ...f, publishing: true }));
    try {
      await sr.metaPublishPost({ connection_id: cid, message: postForm.message, image_url: postForm.image_url || null, link: postForm.link || null });
      toast.success("Posted to your Facebook Page");
      setPostForm({ topic: "", message: "", image_url: "", link: "", connectionId: cid, generating: false, publishing: false });
      loadPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish post"); setPostForm((f) => ({ ...f, publishing: false })); }
  };
  const schedulePost = async () => {
    const cid = postForm.connectionId || (conns[0]?.id);
    if (!cid) { toast.error("Connect a Facebook Page first"); return; }
    if (!postForm.scheduleAt) { toast.error("Pick a date & time"); return; }
    setPostForm((f) => ({ ...f, publishing: true }));
    try {
      await sr.metaSchedulePost({ connection_id: cid, message: postForm.message, image_url: postForm.image_url || null, link: postForm.link || null, scheduled_at: new Date(postForm.scheduleAt).toISOString(), repeat: postForm.repeat === "weekly" ? "weekly" : "none" });
      toast.success(postForm.repeat === "weekly" ? "Weekly post scheduled" : "Post scheduled");
      setPostForm({ topic: "", message: "", image_url: "", link: "", connectionId: cid, scheduleAt: "", repeat: "none", generating: false, publishing: false });
      loadPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); setPostForm((f) => ({ ...f, publishing: false })); }
  };
  const cancelPost = async (id) => { try { await sr.metaCancelPost(id); toast.success("Scheduled post cancelled"); loadPosts(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } };
  const suggestTime = async () => {
    try { const r = await sr.metaBestTime(); setPostForm((f) => ({ ...f, scheduleAt: r.datetime_local })); toast.success(`Best time: ${r.day_label} ${r.time_label}`, { description: r.rationale }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not suggest a time"); }
  };
  const autoScheduleBest = async () => {
    const cid = postForm.connectionId || (conns[0]?.id);
    if (!cid) { toast.error("Connect a Facebook Page first"); return; }
    if (postForm.message.trim().length < 3) { toast.error("Write or generate a post first"); return; }
    setPostForm((f) => ({ ...f, publishing: true }));
    try {
      const r = await sr.metaBestTime();
      await sr.metaSchedulePost({ connection_id: cid, message: postForm.message, image_url: postForm.image_url || null, link: postForm.link || null, scheduled_at: new Date(r.datetime_local).toISOString(), repeat: postForm.repeat === "weekly" ? "weekly" : "none" });
      toast.success(`Scheduled for ${r.day_label} ${r.time_label}`, { description: r.rationale });
      setPostForm({ topic: "", message: "", image_url: "", link: "", connectionId: cid, scheduleAt: "", repeat: "none", generating: false, publishing: false });
      loadPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); setPostForm((f) => ({ ...f, publishing: false })); }
  };
  const reschedulePost = async (id, isoUtc) => { try { await sr.metaReschedulePost(id, isoUtc); toast.success("Post rescheduled"); loadPosts(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); } };
  const retryPost = async (id) => { try { const r = await sr.metaRetryPost(id); r.ok ? toast.success("Post published") : toast.info("Not published"); loadPosts(); } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); } };
  const boostBudget = async (linkId, amount) => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a budget"); return; }
    try { await sr.metaBoostBudget(linkId, { amount: Number(amount), type: "daily" }); toast.success("Ad-set budget updated"); loadPublished(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update budget"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // After a Facebook OAuth redirect (?meta=connected&cid=…), open asset selection.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("meta") === "connected" && q.get("cid")) {
      const cid = q.get("cid");
      sr.metaOauthAssets(cid)
        .then((a) => setOauthSel({ cid, ad_accounts: a.ad_accounts || [], pages: a.pages || [], meta_user: a.meta_user, ad_account_id: "", page_id: "" }))
        .catch(() => toast.error("Could not load your Meta assets"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (q.get("meta") === "error") {
      toast.error(`Facebook connection failed: ${q.get("reason") || "unknown error"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const startOauth = async () => {
    setBusy(true);
    try { const r = await sr.metaOauthUrl(); window.location.href = r.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Facebook connect"); setBusy(false); }
  };
  const finishOauth = async () => {
    if (!oauthSel?.ad_account_id || !oauthSel?.page_id) { toast.error("Select an ad account and a Page"); return; }
    setBusy(true);
    try {
      await sr.metaOauthSelect(oauthSel.cid, { ad_account_id: oauthSel.ad_account_id, page_id: oauthSel.page_id });
      toast.success("Facebook account connected"); setOauthSel(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save selection"); }
    setBusy(false);
  };

  const startWizard = async () => {
    setBusy(true);
    try { setAssets(await sr.metaAssets()); setWizard(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load Meta assets"); }
    setBusy(false);
  };
  const pickPage = async (pageId) => {
    setForm((f) => ({ ...f, page_id: pageId, instagram_actor_id: "" }));
    try { const r = await sr.metaInstagram(pageId); setIgs(r.instagram || []); } catch { setIgs([]); }
  };
  const pickAccount = async (id) => {
    setForm((f) => ({ ...f, ad_account_id: id, dataset_id: "" }));
    try { const r = await sr.metaPixels(id); setPixels(r.pixels || []); } catch { setPixels([]); }
  };
  const save = async () => {
    if (!form.ad_account_id || !form.page_id) { toast.error("Select an ad account and a Page"); return; }
    setBusy(true);
    try {
      const acct = (assets.ad_accounts || []).find((a) => a.id === form.ad_account_id) || {};
      const page = (assets.pages || []).find((p) => p.id === form.page_id) || {};
      const ig = igs.find((i) => i.id === form.instagram_actor_id);
      await sr.metaSelect({
        ...form, ad_account_name: acct.name, page_name: page.name,
        instagram_username: ig?.username, currency: acct.currency, timezone: acct.timezone_name,
        business_name: status?.business_id_masked,
      });
      toast.success("Meta connection saved");
      setWizard(false); setForm({ ad_account_id: "", page_id: "", instagram_actor_id: "", dataset_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save connection"); }
    setBusy(false);
  };
  const validate = async (cid) => { const r = await sr.metaValidate(cid); r.ok ? toast.success("Connection valid") : toast.error(r.error || "Needs attention"); load(); };
  const disconnect = async (cid) => { await sr.metaDisconnect(cid); toast.success("Disconnected"); load(); };
  const loadGrowth = async (cid) => {
    setGrowth((g) => ({ ...g, busy: true }));
    const [saved, recs, capi] = await Promise.all([
      sr.metaSavedAudiences().then((r) => r.audiences || []).catch(() => []),
      sr.metaRecommendations(cid).catch(() => ({ recommendations: [], has_data: false })),
      sr.metaCapiStatus(cid).catch(() => null),
    ]);
    let audiences = [];
    try { audiences = (await sr.metaAudiences(cid)).audiences || []; } catch { /* live fetch optional */ }
    setGrowth((g) => ({ ...g, saved, recs, capi, audiences, pixelInput: "", busy: false, loaded: true }));
  };
  const savePixel = async (cid, autoSend) => {
    if (!growth.pixelInput.trim()) { toast.error("Enter a Pixel / Dataset ID"); return; }
    try {
      const r = await sr.metaCapiSetPixel({ connection_id: cid, pixel_id: growth.pixelInput.trim(), auto_send_leads: autoSend, auto_send_purchases: !!growth.capiPurchases });
      toast.success("Pixel saved for Conversions API");
      setGrowth((g) => ({ ...g, capi: { ...g.capi, configured: true, pixel_id: r.pixel_id, auto_send_leads: r.auto_send_leads, auto_send_purchases: r.auto_send_purchases }, pixelInput: "" }));
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save pixel"); }
  };
  const testCapi = async (cid) => {
    setGrowth((g) => ({ ...g, busy: true }));
    try { const r = await sr.metaCapiTestEvent({ connection_id: cid, event_name: "Lead" }); r.ok ? toast.success(`Test event sent · ${r.events_received} received by Meta`) : toast.error(r.error || "Meta rejected the test event"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send test event"); } finally { setGrowth((g) => ({ ...g, busy: false })); }
  };
  const buildFromLeads = async (cid) => {
    setGrowth((g) => ({ ...g, busy: true }));
    try { const r = await sr.metaAudienceFromLeads({ connection_id: cid, name: growth.audName || "SmartReach leads" }); toast.success(`Audience created · ${r.uploaded} contacts uploaded`); await loadGrowth(cid); setGrowth((g) => ({ ...g, audName: "" })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build audience"); setGrowth((g) => ({ ...g, busy: false })); }
  };
  const buildLookalike = async (cid, aud) => {
    try { const r = await sr.metaAudienceLookalike(aud.meta_audience_id, { connection_id: cid, country: "US", ratio: 0.01, name: `${aud.name} — Lookalike 1%` }); toast.success(`Lookalike created (${r.country} ${Math.round(r.ratio * 100)}%)`); loadGrowth(cid); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build lookalike"); }
  };
  const saveMetaCfg = async () => {
    if (!cfgForm.business_id.trim() && !adminCfg?.business_id) { toast.error("Business ID is required"); return; }
    setCfgBusy(true); setCfgTest(null);
    try {
      const r = await sr.metaAdminConfigSave(cfgForm);
      toast.success("Meta credentials saved");
      const cfg = await sr.metaAdminConfigGet(); setAdminCfg(cfg);
      setCfgForm({ app_id: cfg.app_id || "", business_id: cfg.business_id || "", app_secret: "", system_token: "" });
      setStatus(await sr.metaStatus());
      if (r.configured) { await testMetaCfg(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save credentials"); } finally { setCfgBusy(false); }
  };
  const testMetaCfg = async () => {
    setCfgBusy(true);
    try { const r = await sr.metaAdminConfigTest(); setCfgTest(r); r.ok ? toast.success(r.message || "Connection works") : toast.error(r.error || "Test failed"); }
    catch (e) { const err = e?.response?.data?.detail || "Test failed"; setCfgTest({ ok: false, error: err }); toast.error(err); } finally { setCfgBusy(false); }
  };
  const loadCampaigns = async (cid) => {
    setCampaigns({ cid, loading: true, rows: [] });
    try { const r = await sr.metaCampaigns(cid); setCampaigns({ cid, loading: false, rows: r.campaigns || [] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load campaigns"); setCampaigns(null); }
  };
  const importCamp = async (cid, c) => {
    await sr.metaImport({ connection_id: cid, meta_campaign_id: c.id, name: c.name, objective: c.objective, status: c.status });
    toast.success(`Imported "${c.name}"`); loadCampaigns(cid);
  };
  const syncInsights = async (cid) => {
    setBusy(true);
    try { const r = await sr.metaInsightsSync({ connection_id: cid, date_preset: "last_30d" }); toast.success(`Synced ${r.synced} rows`); const g = await sr.metaInsights(cid); setInsights({ cid, ...g }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setBusy(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }} data-testid="meta-loading"><Loader2 className="sr-spin" /></div>;

  const isStaff = (billing?.statements || []).some((s) => s.raw_media_spend != null);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 4px" }} data-testid="meta-ads-workspace">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Facebook size={22} color="#1877F2" />
        <h1 style={{ margin: 0, fontSize: 24 }}>Meta Ads</h1>
      </div>
      <p className="sr-muted" style={{ marginBottom: 18 }}>Facebook & Instagram paid advertising via the Meta Marketing API. $49.99/mo module + 15% of Meta media spend.</p>

      {/* Status */}
      <div className="sr-card" data-testid="meta-status">
        {status?.configured ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><CheckCircle2 size={18} color="#067647" /> <strong>Meta configured</strong> · API {status.api_version} · Business {status.business_id_masked} · System-user mode</div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="meta-not-configured"><AlertTriangle size={18} color="#B8791B" /> <strong>Not configured</strong> — an administrator must add the Meta system-user token & business id.</div>
        )}
      </div>

      {/* Admin · Meta connection setup (staff only) */}
      {adminCfg && (
        <div className="sr-card" style={{ marginTop: 12, borderLeft: "3px solid #1877F2" }} data-testid="meta-admin-config">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Plug size={16} color="#1877F2" /> <strong>Admin · Meta connection setup</strong>
            {adminCfg.configured ? (
              <span className="sr-badge" style={{ color: "#067647", marginLeft: 6 }} data-testid="meta-cfg-configured">Configured</span>
            ) : (
              <span className="sr-badge" style={{ color: "#B8791B", marginLeft: 6 }} data-testid="meta-cfg-incomplete">Incomplete</span>
            )}
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
            Configure Meta credentials here — no environment variables or redeploy needed. Secrets are encrypted at rest and never shown again after saving.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <label style={{ fontSize: 13 }}>
              <div className="sr-muted" style={{ marginBottom: 4 }}>Meta App ID</div>
              <input className="sr-input" data-testid="meta-cfg-app-id" value={cfgForm.app_id} onChange={(e) => setCfgForm((f) => ({ ...f, app_id: e.target.value }))} placeholder="e.g. 123456789012345" />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="sr-muted" style={{ marginBottom: 4 }}>Business ID</div>
              <input className="sr-input" data-testid="meta-cfg-business-id" value={cfgForm.business_id} onChange={(e) => setCfgForm((f) => ({ ...f, business_id: e.target.value }))} placeholder="e.g. 987654321098765" />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="sr-muted" style={{ marginBottom: 4 }}>App Secret {adminCfg.app_secret_set && <span style={{ color: "#067647" }}>· saved ({adminCfg.app_secret_masked})</span>}</div>
              <input className="sr-input" type="password" data-testid="meta-cfg-app-secret" value={cfgForm.app_secret} onChange={(e) => setCfgForm((f) => ({ ...f, app_secret: e.target.value }))} placeholder={adminCfg.app_secret_set ? "Leave blank to keep current" : "Enter app secret"} />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="sr-muted" style={{ marginBottom: 4 }}>System-User Access Token {adminCfg.system_token_set && <span style={{ color: "#067647" }}>· saved ({adminCfg.system_token_masked})</span>}</div>
              <input className="sr-input" type="password" data-testid="meta-cfg-system-token" value={cfgForm.system_token} onChange={(e) => setCfgForm((f) => ({ ...f, system_token: e.target.value }))} placeholder={adminCfg.system_token_set ? "Leave blank to keep current" : "Enter system-user token"} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveMetaCfg} disabled={cfgBusy} data-testid="meta-cfg-save">
              {cfgBusy ? <Loader2 size={13} className="sr-spin" /> : <CheckCircle2 size={13} />} Save credentials
            </button>
            <button className="sr-btn sr-btn-sm" onClick={testMetaCfg} disabled={cfgBusy || !adminCfg.configured} data-testid="meta-cfg-test">
              <RefreshCw size={13} /> Test connection
            </button>
            {cfgTest && (
              <span style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }} data-testid="meta-cfg-test-result">
                {cfgTest.ok
                  ? <><CheckCircle2 size={14} color="#067647" /> {cfgTest.message || `Connected · ${cfgTest.ad_accounts} ad account(s)`}</>
                  : <><AlertTriangle size={14} color="#B42318" /> {cfgTest.error || "Test failed"}</>}
              </span>
            )}
          </div>
        </div>
      )}
      {billing && (
        <div className="sr-card" style={{ marginTop: 12 }} data-testid="meta-billing">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <DollarSign size={16} color="#067647" /> <strong>Meta Ads billing</strong>
            <span className="sr-muted" style={{ fontSize: 12 }}>· {cur.money(billing.module_fee)}/mo module + managed media{cur.isLocal ? ` · in ${cur.currency}` : ""}</span>
            {isStaff && <button className="sr-btn sr-btn-sm" style={{ marginLeft: "auto" }} onClick={() => nav("/app/meta-statements")} data-testid="meta-statements-link"><BarChart3 size={13} /> All statements</button>}
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {(billing.statements || []).slice(0, 3).map((s) => (
              <div key={s.month} data-testid={`meta-invoice-${s.month}`} style={{ minWidth: 150 }}>
                <div className="sr-muted" style={{ fontSize: 12 }}>{s.month}</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{cur.money(s.total)}</div>
                <div className="sr-muted" style={{ fontSize: 12 }}>Module {cur.money(s.module_fee)} · Media {cur.money(s.media_spend)}{s.raw_media_spend != null ? ` (raw ${cur.money(s.raw_media_spend)}, margin ${cur.money(s.media_markup_amount)})` : ""}</div>
                {isStaff && !invoices.find((i) => i.month === s.month) && (
                  <button className="sr-btn sr-btn-sm" style={{ marginTop: 6 }} onClick={() => generateInvoice(s.month)} data-testid={`meta-finalize-${s.month}`}>Finalize invoice</button>
                )}
              </div>
            ))}
          </div>
          {invoices.length > 0 && (
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid="meta-invoices-list">
              <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Finalized invoices</div>
              {invoices.map((inv) => {
                const last = emailHistory.find((h) => h.month === inv.month && h.status === "sent");
                return (
                  <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: 13, flexWrap: "wrap", gap: 6 }} data-testid={`meta-inv-row-${inv.month}`}>
                    <span>{inv.month} · {cur.money(inv.total)} {last && <span className="sr-badge" style={{ color: "#067647", marginLeft: 6 }}>emailed</span>}</span>
                    <span style={{ display: "flex", gap: 6 }}>
                      <button className="sr-btn sr-btn-sm" onClick={() => downloadInvoicePdf(inv)} data-testid={`meta-inv-pdf-${inv.month}`}><Download size={12} /> PDF</button>
                      <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => emailInvoice(inv.id)} data-testid={`meta-inv-email-${inv.month}`}><Send size={12} /> {last ? "Resend" : "Email"}</button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* Facebook token-expiry reminder banner */}
      {conns.some((c) => c.expiry?.expiring) && (
        <div className="sr-card" data-testid="meta-expiry-banner" style={{ marginTop: 12, borderLeft: "3px solid #B8791B", display: "flex", alignItems: "center", gap: 10 }}>
          <Clock size={18} color="#B8791B" />
          <div style={{ flex: 1, fontSize: 14 }}>
            {conns.filter((c) => c.expiry?.expiring).map((c) => (
              <div key={c.id}>Your Facebook connection <strong>{c.page_name || c.ad_account_name || c.meta_user}</strong> {c.expiry.days_until_expiry < 0 ? "has expired" : `expires in ${c.expiry.days_until_expiry} day(s)`} — reconnect to keep campaigns running.</div>
            ))}
          </div>
          <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={startOauth} data-testid="meta-reconnect"><Facebook size={13} /> Reconnect</button>
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 17, margin: 0 }}>Connections</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="sr-btn sr-btn-sm" onClick={startOauth} disabled={busy} data-testid="meta-connect-facebook"><Facebook size={14} color="#1877F2" /> Connect Facebook (your account)</button>
          {status?.configured && <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={startWizard} disabled={busy} data-testid="meta-connect-btn"><Plug size={14} /> Connect Meta assets</button>}
        </div>
      </div>

      {/* Facebook OAuth asset selection (after redirect back) */}
      {oauthSel && (
        <div style={overlay} data-testid="meta-oauth-select">
          <div className="sr-card" style={{ width: 560, maxWidth: "100%" }}>
            <h3 style={{ marginTop: 0 }}>Choose your Meta assets</h3>
            <p className="sr-muted" style={{ fontSize: 13 }}>Connected as {oauthSel.meta_user || "your Facebook account"}. Pick the ad account and Page to run ads with.</p>
            <Field label="Ad account">
              <select className="sr-select" value={oauthSel.ad_account_id} onChange={(e) => setOauthSel({ ...oauthSel, ad_account_id: e.target.value })} data-testid="meta-oauth-account">
                <option value="">Select an ad account…</option>
                {oauthSel.ad_accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
              </select>
              {oauthSel.ad_accounts.length === 0 && <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>No ad accounts were granted. Re-connect and allow ad account access.</p>}
            </Field>
            <Field label="Facebook Page">
              <select className="sr-select" value={oauthSel.page_id} onChange={(e) => setOauthSel({ ...oauthSel, page_id: e.target.value })} data-testid="meta-oauth-page">
                <option value="">Select a Page…</option>
                {oauthSel.pages.map((p) => <option key={p.page_id} value={p.page_id}>{p.name}{p.instagram_username ? ` · @${p.instagram_username}` : ""}</option>)}
              </select>
            </Field>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="sr-btn sr-btn-primary" onClick={finishOauth} disabled={busy} data-testid="meta-oauth-save">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save & connect"}</button>
              <button className="sr-btn" onClick={() => setOauthSel(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {conns.length === 0 && !wizard && <div className="sr-card" style={{ marginTop: 10 }} data-testid="meta-empty">No Meta connection yet. Click “Connect Meta assets” to select your ad account and Page.</div>}

      {conns.map((c) => (
        <div key={c.id} className="sr-card" style={{ marginTop: 10 }} data-testid={`meta-conn-${c.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{c.page_name || "Page"} {c.instagram_username ? `· @${c.instagram_username}` : ""}</div>
              <div className="sr-muted" style={{ fontSize: 13 }}>Ad account {c.ad_account_id} · {c.currency || ""} · <span style={{ color: c.status === "connected" ? "#067647" : "#B8791B" }}>{c.status}</span></div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="sr-btn sr-btn-sm" onClick={() => validate(c.id)} data-testid={`meta-validate-${c.id}`}><RefreshCw size={13} /> Validate</button>
              <button className="sr-btn sr-btn-sm" onClick={() => loadCampaigns(c.id)} data-testid={`meta-import-open-${c.id}`}><Download size={13} /> Import campaigns</button>
              <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => { setBuilder(c.id); setDraft(emptyDraft); }} data-testid={`meta-build-${c.id}`}>+ Build campaign</button>
              <button className="sr-btn sr-btn-sm" onClick={() => syncInsights(c.id)} disabled={busy} data-testid={`meta-sync-${c.id}`}><BarChart3 size={13} /> Sync insights</button>
              <button className="sr-btn sr-btn-sm" onClick={() => disconnect(c.id)} data-testid={`meta-disconnect-${c.id}`}>Disconnect</button>
            </div>
          </div>

          {campaigns?.cid === c.id && (
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid="meta-campaign-list">
              {campaigns.loading ? <Loader2 className="sr-spin" /> : campaigns.rows.length === 0 ? <div className="sr-muted">No Meta campaigns found in this ad account.</div> : campaigns.rows.map((mc2) => (
                <div key={mc2.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 14 }}>
                  <span>{mc2.name} <span className="sr-muted">· {mc2.objective || "—"} · {mc2.effective_status || mc2.status}</span></span>
                  {mc2.imported ? <span className="sr-badge">Imported</span> : <button className="sr-btn sr-btn-sm" onClick={() => importCamp(c.id, mc2)} data-testid={`meta-import-${mc2.id}`}>Import</button>}
                </div>
              ))}
            </div>
          )}

          {insights?.cid === c.id && insights.totals && (
            <div style={{ marginTop: 12, display: "flex", gap: 20, flexWrap: "wrap" }} data-testid="meta-insights">
              <Metric label="Meta spend" v={cur.money(insights.totals.spend)} />
              <Metric label="Impressions" v={Number(insights.totals.impressions || 0).toLocaleString()} />
              <Metric label="Clicks" v={Number(insights.totals.clicks || 0).toLocaleString()} />
              <Metric label="Reach" v={Number(insights.totals.reach || 0).toLocaleString()} />
            </div>
          )}
        </div>
      ))}

      {/* Wizard */}
      {wizard && assets && (
        <div className="sr-card" style={{ marginTop: 12 }} data-testid="meta-wizard">
          <h3 style={{ marginTop: 0 }}>Select your Meta assets</h3>
          <Field label="Ad account">
            <select className="sr-select" value={form.ad_account_id} onChange={(e) => pickAccount(e.target.value)} data-testid="meta-select-account">
              <option value="">Select an ad account…</option>
              {(assets.ad_accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
            </select>
            {(assets.ad_accounts || []).length === 0 && <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>No ad accounts are assigned to the SmartReach system user for this business yet.</p>}
          </Field>
          <Field label="Facebook Page">
            <select className="sr-select" value={form.page_id} onChange={(e) => pickPage(e.target.value)} data-testid="meta-select-page">
              <option value="">Select a Page…</option>
              {(assets.pages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          {igs.length > 0 && (
            <Field label="Instagram (optional)">
              <select className="sr-select" value={form.instagram_actor_id} onChange={(e) => setForm((f) => ({ ...f, instagram_actor_id: e.target.value }))} data-testid="meta-select-ig">
                <option value="">None</option>
                {igs.map((i) => <option key={i.id} value={i.id}>@{i.username}</option>)}
              </select>
            </Field>
          )}
          {pixels.length > 0 && (
            <Field label="Dataset / Pixel (optional)">
              <select className="sr-select" value={form.dataset_id} onChange={(e) => setForm((f) => ({ ...f, dataset_id: e.target.value }))} data-testid="meta-select-pixel">
                <option value="">None</option>
                {pixels.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="sr-btn sr-btn-primary" onClick={save} disabled={busy} data-testid="meta-save-connection">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save connection"}</button>
            <button className="sr-btn" onClick={() => setWizard(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* PHASE 6 & 7 — Growth tools: Audiences, Conversions API, Recommendations */}
      {conns.length > 0 && (() => {
        const cid = conns[0].id;
        const g = growth;
        return (
          <div className="sr-card" style={{ marginTop: 14, borderLeft: "3px solid #7C3AED" }} data-testid="meta-growth-tools">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Sparkles size={16} color="#7C3AED" /> <strong>Growth tools</strong>
              <span className="sr-muted" style={{ fontSize: 12 }}>· Audiences, Conversions API & optimization</span>
              <button className="sr-btn sr-btn-sm" style={{ marginLeft: "auto" }} onClick={() => loadGrowth(cid)} disabled={g.busy} data-testid="meta-growth-refresh"><RefreshCw size={12} /> Refresh</button>
            </div>

            {/* Recommendations */}
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid="meta-recommendations">
              <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={14} color="#067647" /> Recommendations</div>
              {!g.recs || !g.recs.has_data ? (
                <p className="sr-muted" style={{ fontSize: 13, marginBottom: 0 }} data-testid="meta-recs-empty">Sync campaign insights to get optimization tips based on real performance.</p>
              ) : (g.recs.recommendations || []).length === 0 ? (
                <p className="sr-muted" style={{ fontSize: 13, marginBottom: 0 }} data-testid="meta-recs-none">All clear — no issues detected across {g.recs.campaigns_analyzed} campaign(s).</p>
              ) : (
                g.recs.recommendations.map((r, i) => (
                  <div key={i} data-testid={`meta-rec-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", fontSize: 13 }}>
                    <span className="sr-badge" style={{ color: r.severity === "high" ? "#B42318" : r.severity === "opportunity" ? "#067647" : "#B8791B", whiteSpace: "nowrap" }}>{r.severity === "opportunity" ? "opportunity" : r.severity}</span>
                    <span>{r.message} <span className="sr-muted">({r.metric})</span></span>
                  </div>
                ))
              )}
            </div>

            {/* Audiences */}
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid="meta-audiences">
              <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><Users size={14} color="#1877F2" /> Custom & Lookalike audiences</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
                <input className="sr-input" style={{ maxWidth: 220 }} placeholder="Audience name (optional)" value={g.audName} onChange={(e) => setGrowth((s) => ({ ...s, audName: e.target.value }))} data-testid="meta-aud-name" />
                <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => buildFromLeads(cid)} disabled={g.busy} data-testid="meta-aud-build">{g.busy ? <Loader2 size={12} className="sr-spin" /> : <Users size={12} />} Build from my leads</button>
              </div>
              {(g.saved || []).length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="meta-aud-saved-list">
                  {g.saved.map((a) => (
                    <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: 13, flexWrap: "wrap", gap: 6 }} data-testid={`meta-aud-${a.id}`}>
                      <span>{a.name} <span className="sr-badge" style={{ marginLeft: 4, color: a.subtype === "LOOKALIKE" ? "#7C3AED" : "#1877F2" }}>{a.subtype}</span>{a.uploaded ? <span className="sr-muted"> · {a.uploaded} contacts</span> : null}</span>
                      {a.subtype === "CUSTOM" && <button className="sr-btn sr-btn-sm" onClick={() => buildLookalike(cid, a)} data-testid={`meta-aud-lookalike-${a.id}`}><Target size={12} /> Create lookalike (1%)</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Conversions API */}
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid="meta-capi">
              <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><Zap size={14} color="#B8791B" /> Conversions API (server-side events)</div>
              {g.capi?.configured ? (
                <p className="sr-muted" style={{ fontSize: 13, marginBottom: 6 }} data-testid="meta-capi-configured">Pixel {g.capi.pixel_id} connected. {g.capi.auto_send_leads ? "New leads auto-send a server-side Lead event." : "Auto-send for leads is off."} {g.capi.auto_send_purchases ? "Matched POS orders auto-send a Purchase event." : ""}</p>
              ) : (
                <p className="sr-muted" style={{ fontSize: 13, marginBottom: 6 }} data-testid="meta-capi-unconfigured">Add your Pixel / Dataset ID to send conversions to Meta directly from the server (more reliable than the browser pixel). Every event carries a stable event_id so it de-duplicates against your browser Pixel.</p>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--navy)", marginBottom: 8 }} data-testid="meta-capi-purchases-toggle">
                <input type="checkbox" checked={!!g.capiPurchases || !!g.capi?.auto_send_purchases}
                  onChange={(e) => setGrowth((s) => ({ ...s, capiPurchases: e.target.checked }))} data-testid="meta-capi-purchases-checkbox" />
                Also auto-send Purchase events when POS orders match a coupon
              </label>
              {g.capi?.outbox && (g.capi.outbox.sent || g.capi.outbox.pending || g.capi.outbox.retry || g.capi.outbox.failed) ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }} data-testid="meta-capi-outbox">
                  <span className="sr-badge sr-badge-green">Sent: {g.capi.outbox.sent || 0}</span>
                  <span className="sr-badge sr-badge-gray">Pending: {(g.capi.outbox.pending || 0) + (g.capi.outbox.retry || 0)}</span>
                  {g.capi.outbox.failed ? <span className="sr-badge" style={{ background: "#FDECEC", color: "#B42318" }}>Failed: {g.capi.outbox.failed}</span> : null}
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input className="sr-input" style={{ maxWidth: 220 }} placeholder="Pixel / Dataset ID" value={g.pixelInput} onChange={(e) => setGrowth((s) => ({ ...s, pixelInput: e.target.value }))} data-testid="meta-capi-pixel-input" />
                <button className="sr-btn sr-btn-sm" onClick={() => savePixel(cid, true)} disabled={g.busy} data-testid="meta-capi-save">Save + auto-send</button>
                <button className="sr-btn sr-btn-sm" onClick={() => testCapi(cid)} disabled={g.busy || !g.capi?.configured} data-testid="meta-capi-test"><Send size={12} /> Send test event</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Drafts / Campaign Builder */}
      {conns.length > 0 && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="meta-drafts">
          <h3 style={{ marginTop: 0 }}>Campaign drafts</h3>
          {drafts.length === 0 ? <div className="sr-muted">No drafts yet. Click “Build campaign” on a connection to create one.</div> : drafts.map((d) => (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 8 }} data-testid={`meta-draft-${d.id}`}>
              <div>
                <div style={{ fontWeight: 700 }}>{d.name} <span className="sr-badge" style={{ marginLeft: 6 }}>{d.status}</span></div>
                <div className="sr-muted" style={{ fontSize: 13 }}>{d.objective} · {(d.budget || {}).type} ${(d.budget || {}).amount}/day</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.status !== "published"
                  ? <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => publishDraft(d)} data-testid={`meta-publish-${d.id}`}>Publish (PAUSED)</button>
                  : <span className="sr-badge" style={{ color: "#067647" }}>Published · PAUSED</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Published campaigns — A/B winner report + auto-pause */}
      {conns.length > 0 && published.length > 0 && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="meta-published">
          <h3 style={{ marginTop: 0 }}>Published campaigns</h3>
          {published.map((p) => (
            <div key={p.id} style={{ padding: "10px 0", borderTop: "1px solid var(--border)" }} data-testid={`meta-pub-${p.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{p.name}
                    <span className="sr-badge" style={{ marginLeft: 6 }}>{p.status}</span>
                    {p.ab_test && <span className="sr-badge" style={{ marginLeft: 6, color: "#1877F2" }}>A/B · {(p.ads || []).length} ads</span>}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 13 }}>{(p.objective || "").replace("OUTCOME_", "")}{p.lead_form_id ? " · lead form" : ""}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="sr-btn sr-btn-sm" onClick={() => openReport(p.id)} data-testid={`meta-report-${p.id}`}><Trophy size={13} /> Report</button>
                  {p.ab_test && <button className="sr-btn sr-btn-sm" onClick={() => autoPause(p.id)} data-testid={`meta-autopause-${p.id}`}><Pause size={13} /> Auto-pause losers</button>}
                  {p.status === "PAUSED"
                    ? (isAdmin
                        ? <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => activateLink(p.id)} data-testid={`meta-activate-${p.id}`}>Activate</button>
                        : <span className="sr-badge sr-badge-amber" data-testid={`meta-awaiting-golive-${p.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Clock size={12} /> Waiting for admin approval to go live</span>)
                    : (isAdmin
                        ? <button className="sr-btn sr-btn-sm" onClick={() => pauseLink(p.id)} data-testid={`meta-pause-${p.id}`}>Pause</button>
                        : null)}
                </div>
              </div>
              {report?.linkId === p.id && (
                <div style={{ marginTop: 10, background: "var(--bg-subtle, #f7f9fc)", borderRadius: 8, padding: 10 }} data-testid={`meta-report-panel-${p.id}`}>
                  {report.loading ? <Loader2 className="sr-spin" size={16} />
                    : !report.available ? <div className="sr-muted" style={{ fontSize: 13 }}>No performance data yet{report.reason ? ` — ${report.reason}` : ""}. Reports appear once the ads have delivered.</div>
                      : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th>Ad</th><th>Spend</th><th>Clicks</th><th>Results</th><th>{report.result_label}</th></tr></thead>
                          <tbody>
                            {report.ads.map((a) => (
                              <tr key={a.ad_id} style={{ borderTop: "1px solid var(--border)", fontWeight: a.ad_id === report.winner_ad_id ? 700 : 400 }}>
                                <td>{a.ad_id === report.winner_ad_id && <Trophy size={12} color="#067647" style={{ verticalAlign: "-2px", marginRight: 4 }} />}{a.ad_name || a.ad_id}</td>
                                <td>${a.spend}</td><td>{a.clicks}</td><td>{a.results}</td>
                                <td>{a.cost_per_result != null ? `$${a.cost_per_result}` : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  {report.available && (report.paused_ads || []).length > 0 && <div className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Auto-paused {report.paused_ads.length} losing ad(s).</div>}
                </div>
              )}
              {p.reallocated_to && (
                <div style={{ marginTop: 8, fontSize: 13, color: "#067647", display: "flex", alignItems: "center", gap: 6 }} data-testid={`meta-realloc-${p.id}`}>
                  <Trophy size={13} /> Budget reallocated → winning ad now receives 100% of the shared ad-set budget.
                </div>
              )}
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <span className="sr-muted" style={{ fontSize: 12 }}>Boost daily budget:</span>
                <input className="sr-input" type="number" placeholder="$/day" style={{ width: 100 }}
                  value={budgetInputs[p.id] || ""} onChange={(e) => setBudgetInputs({ ...budgetInputs, [p.id]: e.target.value })}
                  data-testid={`meta-budget-input-${p.id}`} />
                <button className="sr-btn sr-btn-sm" onClick={() => boostBudget(p.id, budgetInputs[p.id])} data-testid={`meta-budget-set-${p.id}`}>Update</button>
                {p.budget?.amount && <span className="sr-muted" style={{ fontSize: 12 }}>current ${p.budget.amount}/{p.budget.type}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Facebook content post */}
      {conns.length > 0 && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="meta-content-post">
          <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}><Sparkles size={16} color="#1877F2" /> AI Facebook post</h3>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: -4 }}>Write an organic post with AI and publish it straight to your Facebook Page.</p>
          {conns.length > 1 && (
            <Field label="Post to">
              <select className="sr-select" value={postForm.connectionId || conns[0].id} onChange={(e) => setPostForm({ ...postForm, connectionId: e.target.value })} data-testid="post-connection">
                {conns.map((c) => <option key={c.id} value={c.id}>{c.page_name || c.ad_account_name || c.id}</option>)}
              </select>
            </Field>
          )}
          <Field label="What's it about? (optional)">
            <div style={{ display: "flex", gap: 8 }}>
              <input className="sr-input" value={postForm.topic} onChange={(e) => setPostForm({ ...postForm, topic: e.target.value })} placeholder="e.g. weekend 20% off sale" data-testid="post-topic" />
              <button className="sr-btn sr-btn-primary" onClick={genPost} disabled={postForm.generating} data-testid="post-generate">{postForm.generating ? <Loader2 size={14} className="sr-spin" /> : <><Sparkles size={14} /> Generate</>}</button>
            </div>
          </Field>
          <Field label="Post text"><textarea className="sr-input" rows={4} value={postForm.message} onChange={(e) => setPostForm({ ...postForm, message: e.target.value })} placeholder="Your post…" data-testid="post-message" /></Field>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Field label="Image URL (optional)"><input className="sr-input" value={postForm.image_url} onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })} placeholder="https://…" data-testid="post-image" style={{ minWidth: 220 }} /></Field>
            <Field label="Link (optional)"><input className="sr-input" value={postForm.link} onChange={(e) => setPostForm({ ...postForm, link: e.target.value })} placeholder="https://…" data-testid="post-link" style={{ minWidth: 220 }} /></Field>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <button className="sr-btn sr-btn-primary" onClick={publishPost} disabled={postForm.publishing || postForm.message.trim().length < 3} data-testid="post-publish">{postForm.publishing ? <Loader2 size={14} className="sr-spin" /> : <><Send size={14} /> Post now</>}</button>
            <Field label="Or schedule for (CT)"><input className="sr-input" type="datetime-local" value={postForm.scheduleAt} onChange={(e) => setPostForm({ ...postForm, scheduleAt: e.target.value })} data-testid="post-schedule-at" /></Field>
            <button className="sr-btn" onClick={schedulePost} disabled={postForm.publishing || postForm.message.trim().length < 3 || !postForm.scheduleAt} data-testid="post-schedule"><Clock size={14} /> Schedule</button>
            <button className="sr-btn" onClick={suggestTime} data-testid="post-best-time"><Sparkles size={14} /> Suggest best time</button>
            <button className="sr-btn sr-btn-primary" onClick={autoScheduleBest} disabled={postForm.publishing || postForm.message.trim().length < 3} data-testid="post-autoschedule"><Sparkles size={14} /> Schedule at best time</button>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 8, cursor: "pointer" }} data-testid="post-repeat-label">
              <input type="checkbox" checked={postForm.repeat === "weekly"} onChange={(e) => setPostForm({ ...postForm, repeat: e.target.checked ? "weekly" : "none" })} data-testid="post-repeat" /> Repeat weekly
            </label>
          </div>
          {posts.filter((p) => p.status === "failed").length > 0 && (
            <div style={{ marginTop: 12 }} data-testid="meta-failed-list">
              <div className="sr-muted" style={{ fontSize: 12, marginBottom: 4, color: "#B42318" }}>Failed to publish</div>
              {posts.filter((p) => p.status === "failed").map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13, gap: 8 }} data-testid={`meta-failed-${p.id}`}>
                  <span><AlertTriangle size={12} color="#B42318" style={{ verticalAlign: "-2px", marginRight: 4 }} />{(p.message || "").slice(0, 45)}… <span className="sr-muted">— {p.error || "error"}</span></span>
                  <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => retryPost(p.id)} data-testid={`meta-retry-${p.id}`}><RefreshCw size={12} /> Retry</button>
                </div>
              ))}
            </div>
          )}
          {posts.filter((p) => p.status === "scheduled").length > 0 && (
            <div style={{ marginTop: 12 }} data-testid="meta-scheduled-list">
              <div className="sr-muted" style={{ fontSize: 12, marginBottom: 4 }}>Scheduled</div>
              {posts.filter((p) => p.status === "scheduled").map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13, gap: 8 }} data-testid={`meta-scheduled-${p.id}`}>
                  <span><Clock size={12} color="#B8791B" style={{ verticalAlign: "-2px", marginRight: 4 }} />{new Date(p.scheduled_at).toLocaleString()}{p.repeat === "weekly" ? " · weekly" : ""} · {(p.message || "").slice(0, 50)}…</span>
                  <button className="sr-btn sr-btn-sm" onClick={() => cancelPost(p.id)} data-testid={`meta-cancel-${p.id}`}>Cancel</button>
                </div>
              ))}
            </div>
          )}
          {posts.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {posts.filter((p) => p.status === "published").slice(0, 5).map((p) => (
                <div key={p.id} style={{ padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13 }}>
                  <CheckCircle2 size={13} color="#067647" style={{ verticalAlign: "-2px", marginRight: 4 }} />{(p.message || "").slice(0, 90)}…
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Post calendar */}
      {conns.length > 0 && posts.some((p) => p.status === "scheduled") && (
        <PostCalendar month={calMonth} setMonth={setCalMonth} posts={posts} onCancel={cancelPost} onReschedule={reschedulePost} />
      )}

      {/* Leads from Meta Lead Ads */}
      {conns.length > 0 && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="meta-leads">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Meta Lead Ads → SmartReach leads</h3>
            <button className="sr-btn sr-btn-sm" onClick={loadLeads}><RefreshCw size={13} /> Refresh</button>
          </div>
          {leads == null ? <div className="sr-muted" style={{ marginTop: 8 }}>Loading…</div>
            : leads.length === 0 ? <div className="sr-muted" style={{ marginTop: 8 }}>No lead-ad leads yet. New Lead Ads arrive automatically via your Meta webhook.</div>
              : leads.slice(0, 25).map((l) => (
                <div key={l.leadgen_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 14, borderTop: "1px solid var(--border)" }}>
                  <span>{(l.fields || {}).full_name || (l.fields || {}).email || l.leadgen_id}</span>
                  <span className="sr-badge" style={{ color: l.imported ? "#067647" : "#B8791B" }}>{l.imported ? "Imported" : "Received"}</span>
                </div>
              ))}
        </div>
      )}

      {/* Campaign builder modal */}
      {builder && (
        <div style={overlay} data-testid="meta-builder">
          <div className="sr-card" style={{ width: 620, maxWidth: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ marginTop: 0 }}>Build Meta campaign (draft)</h3>
            <Field label="Campaign name"><input className="sr-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="draft-name" /></Field>
            <Field label="Objective">
              <select className="sr-select" value={draft.objective} onChange={(e) => setDraft({ ...draft, objective: e.target.value })} data-testid="draft-objective">
                {["OUTCOME_TRAFFIC", "OUTCOME_LEADS", "OUTCOME_ENGAGEMENT", "OUTCOME_AWARENESS", "OUTCOME_SALES"].map((o) => <option key={o} value={o}>{o.replace("OUTCOME_", "")}</option>)}
              </select>
            </Field>
            <div style={{ display: "flex", gap: 10 }}>
              <Field label="Country"><input className="sr-input" value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })} data-testid="draft-country" style={{ width: 90 }} /></Field>
              <Field label="Age min"><input className="sr-input" type="number" value={draft.age_min} onChange={(e) => setDraft({ ...draft, age_min: e.target.value })} style={{ width: 80 }} /></Field>
              <Field label="Age max"><input className="sr-input" type="number" value={draft.age_max} onChange={(e) => setDraft({ ...draft, age_max: e.target.value })} style={{ width: 80 }} /></Field>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Field label="Budget type"><select className="sr-select" value={draft.budget_type} onChange={(e) => setDraft({ ...draft, budget_type: e.target.value })}><option value="daily">Daily</option><option value="lifetime">Lifetime</option></select></Field>
              <Field label="Amount ($)"><input className="sr-input" type="number" value={draft.budget_amount} onChange={(e) => setDraft({ ...draft, budget_amount: e.target.value })} data-testid="draft-budget" style={{ width: 120 }} /></Field>
            </div>
            <Field label="Primary text"><textarea className="sr-input" rows={2} value={draft.primary_text} onChange={(e) => setDraft({ ...draft, primary_text: e.target.value })} data-testid="draft-primary-text" /></Field>
            <Field label="Headline"><input className="sr-input" value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} /></Field>
            <Field label="Destination link"><input className="sr-input" value={draft.link_url} onChange={(e) => setDraft({ ...draft, link_url: e.target.value })} placeholder="https://…" data-testid="draft-link" /></Field>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="sr-btn sr-btn-primary" onClick={() => saveDraft(builder)} disabled={busy} data-testid="draft-save">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save draft"}</button>
              <button className="sr-btn" onClick={() => setBuilder(null)}>Cancel</button>
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Saved as a draft only. Publishing creates it in Meta <strong>PAUSED</strong> — no spend until you activate.</p>
          </div>
        </div>
      )}
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(10,18,28,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, zIndex: 100, overflow: "auto" };

const Field = ({ label, children }) => (
  <div className="sr-field" style={{ marginBottom: 12 }}>
    <label className="sr-label">{label}</label>
    {children}
  </div>
);
const Metric = ({ label, v }) => (
  <div><div className="sr-muted" style={{ fontSize: 12 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{v}</div></div>
);

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK = 7 * 24 * 60 * 60 * 1000;

const PostCalendar = ({ month, setMonth, posts, onCancel, onReschedule }) => {
  const [dragId, setDragId] = React.useState(null);
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const byDay = {};
  const key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const add = (d, ev) => { (byDay[key(d)] = byDay[key(d)] || []).push(ev); };
  posts.filter((p) => p.status === "scheduled").forEach((p) => {
    const base = new Date(p.scheduled_at);
    if (p.repeat === "weekly") {
      let d = new Date(base);
      while (d < monthStart) d = new Date(d.getTime() + WEEK);
      while (d <= monthEnd) { add(d, { id: p.id, msg: p.message, recurring: true, at: new Date(d), real: key(d) === key(base), src: p }); d = new Date(d.getTime() + WEEK); }
    } else if (base >= monthStart && base <= monthEnd) {
      add(base, { id: p.id, msg: p.message, at: base, real: true, src: p });
    }
  });
  const dropOnDay = (d) => {
    if (!dragId || !onReschedule) return;
    const p = posts.find((x) => x.id === dragId);
    if (!p) { setDragId(null); return; }
    const orig = new Date(p.scheduled_at);
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate(), orig.getHours(), orig.getMinutes(), 0, 0);
    setDragId(null);
    if (key(target) !== key(orig)) onReschedule(p.id, target.toISOString());
  };
  const cells = [];
  for (let i = 0; i < monthStart.getDay(); i++) cells.push(null);
  for (let day = 1; day <= monthEnd.getDate(); day++) cells.push(new Date(month.getFullYear(), month.getMonth(), day));
  const today = new Date();
  return (
    <div className="sr-card" style={{ marginTop: 14 }} data-testid="meta-post-calendar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Post calendar</h3>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="sr-btn sr-btn-sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev">‹</button>
          <span style={{ fontWeight: 700, minWidth: 140, textAlign: "center" }} data-testid="cal-label">{month.toLocaleString(undefined, { month: "long", year: "numeric" })}</span>
          <button className="sr-btn sr-btn-sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next">›</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {DOW.map((d) => <div key={d} className="sr-muted" style={{ fontSize: 11, textAlign: "center", fontWeight: 700 }}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const evs = byDay[key(d)] || [];
          const isToday = key(d) === key(today);
          return (
            <div key={key(d)}
              onDragOver={(e) => { if (dragId) e.preventDefault(); }}
              onDrop={() => dropOnDay(d)}
              style={{ minHeight: 64, border: "1px solid var(--border)", borderRadius: 6, padding: 4, background: isToday ? "rgba(24,119,242,.06)" : "transparent" }} data-testid={`cal-day-${d.getDate()}`}>
              <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? "#1877F2" : "var(--muted)" }}>{d.getDate()}</div>
              {evs.slice(0, 3).map((ev, j) => (
                <div key={j} title={ev.real ? `${ev.msg} — drag to reschedule` : ev.msg}
                  draggable={!!ev.real && !!onReschedule}
                  onDragStart={() => ev.real && setDragId(ev.id)}
                  onDragEnd={() => setDragId(null)}
                  style={{ fontSize: 10, marginTop: 2, padding: "1px 3px", borderRadius: 3, background: ev.recurring ? "rgba(24,119,242,.12)" : "rgba(184,121,27,.14)", color: ev.recurring ? "#1877F2" : "#8a5a12", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 3, cursor: ev.real ? "grab" : "default" }}>
                  {ev.recurring ? <RefreshCw size={9} /> : <Clock size={9} />}
                  {ev.at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} {ev.msg?.slice(0, 18)}
                  {ev.real && onCancel && <span onClick={() => onCancel(ev.id)} style={{ marginLeft: "auto", cursor: "pointer", fontWeight: 700 }} data-testid={`cal-cancel-${ev.id}`}>×</span>}
                </div>
              ))}
              {evs.length > 3 && <div className="sr-muted" style={{ fontSize: 9 }}>+{evs.length - 3} more</div>}
            </div>
          );
        })}
      </div>
      <div className="sr-muted" style={{ fontSize: 11, marginTop: 8, display: "flex", gap: 14 }}>
        <span><Clock size={10} style={{ verticalAlign: "-1px" }} /> one-off</span>
        <span style={{ color: "#1877F2" }}><RefreshCw size={10} style={{ verticalAlign: "-1px" }} /> recurring (weekly)</span>
        <span>· drag a one-off post to another day to reschedule</span>
      </div>
    </div>
  );
};
